import React, { useState, useRef } from "react";
import {
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Breadcrumbs,
  Anchor,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { RichTextEditor, Link as TiptapLink } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table as TiptapTable } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Image as ImageExtension } from "@tiptap/extension-image";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { useForm } from "@mantine/form";
import {
  IconPhoto,
  IconTable,
  IconX,
  IconSend,
  IconChevronRight,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { broadcastNewsletter } from "../../../../services/newsletterService";
import { uploadImage } from "../../../../services/upload";
import { DefaultErrorNotification, DefaultSuccessNotification } from "../../../../lib/methods";
import "./styles.scss";

type EmailFormValues = {
  subject: string;
  content: string;
};

export default function NewEmailPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<EmailFormValues>({
    initialValues: {
      subject: "",
      content: "",
    },
    validate: {
      subject: (v) => (v.trim().length ? null : "Subject is required"),
      content: (v) => (v.trim().length > 10 ? null : "Content is too short"),
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TiptapTable.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      ImageExtension,
    ],
    content: "",
    onUpdate: ({ editor }) => {
      form.setFieldValue("content", editor.getHTML());
    },
  });

  async function handleInlineImageUpload(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImage(file, "emails");
      (editor?.chain().focus() as any).setImage({ src: result.url }).run();
    } catch (error) {
      console.error("Failed to upload inline image:", error);
      DefaultErrorNotification("Failed to upload image. Please try again.", "Upload Failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploading(false);
    }
  }

  function handleConfirmSend() {
    const valid = form.validate();
    if (valid.hasErrors) return;
    open();
  }

  async function handleSend() {
    close();
    setLoading(true);
    try {
      const result = await broadcastNewsletter({
        subject: form.values.subject,
        content: form.values.content,
      });

      if (result.status === 200) {
        DefaultSuccessNotification(result.message, "Broadcast Successful");
        navigate("/d/emails");
      }
    } catch (error: any) {
      console.error("Broadcast error:", error);
      DefaultErrorNotification(error.message || "An unexpected error occurred", "Broadcast Failed");
    } finally {
      setLoading(false);
    }
  }

  const items = [
    { title: "Dashboard", href: "/d" },
    { title: "Emails", href: "/d/emails" },
    { title: "New Broadcast", href: "/d/emails/new" },
  ].map((item, index) => (
    <Anchor href={item.href} key={index} size="sm" c="dimmed">
      {item.title}
    </Anchor>
  ));

  return (
    <div className="new-email-container">
      <Stack gap="xl">
        <Stack gap="xs">
          <Breadcrumbs separator={<IconChevronRight size={14} />}>
            {items}
          </Breadcrumbs>
          <Title order={2}>Compose Newsletter Broadcast</Title>
          <Text c="dimmed">
            Create and send a rich-text email to all your active subscribers
          </Text>
        </Stack>

        <Paper p="xl" radius="md" shadow="xs" withBorder>
          <Stack gap="lg">
            <TextInput
              label="Email Subject"
              placeholder="e.g. Weekly Health Update - Ufamilymedicine"
              size="md"
              radius="md"
              {...form.getInputProps("subject")}
              required
            />

            <Stack gap={6}>
              <Text size="sm" fw={500}>
                Message Content
              </Text>
              <Paper withBorder radius="md" className="editor-wrapper">
                <RichTextEditor editor={editor}>
                  <RichTextEditor.Toolbar sticky stickyOffset={0}>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Bold />
                      <RichTextEditor.Italic />
                      <RichTextEditor.Underline />
                      <RichTextEditor.Strikethrough />
                      <RichTextEditor.ClearFormatting />
                    </RichTextEditor.ControlsGroup>
                    
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.H1 />
                      <RichTextEditor.H2 />
                      <RichTextEditor.H3 />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.AlignLeft />
                      <RichTextEditor.AlignCenter />
                      <RichTextEditor.AlignJustify />
                      <RichTextEditor.AlignRight />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.BulletList />
                      <RichTextEditor.OrderedList />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Link />
                      <RichTextEditor.Unlink />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Control
                        onClick={() => fileInputRef.current?.click()}
                        title="Insert image"
                      >
                        <IconPhoto size="1rem" stroke={1.5} />
                      </RichTextEditor.Control>
                      <RichTextEditor.Control
                        onClick={() =>
                          (editor?.chain().focus() as any)
                            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                            .run()
                        }
                        title="Insert table"
                      >
                        <IconTable size="1rem" stroke={1.5} />
                      </RichTextEditor.Control>
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Undo />
                      <RichTextEditor.Redo />
                    </RichTextEditor.ControlsGroup>
                  </RichTextEditor.Toolbar>
                  
                  <RichTextEditor.Content mih={300} />
                </RichTextEditor>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={fileInputRef}
                  onChange={handleInlineImageUpload}
                />
              </Paper>
              {form.errors.content && (
                <Text size="xs" c="red">
                  {form.errors.content}
                </Text>
              )}
            </Stack>

            <Group justify="flex-end" mt="xl">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => navigate("/d/emails")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                leftSection={<IconSend size={18} />}
                size="md"
                radius="md"
                color="teal"
                loading={loading}
                onClick={handleConfirmSend}
                className="bg-gradient-to-r from-teal-600 to-cyan-600"
              >
                Send Broadcast Now
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>

      <Modal 
        opened={opened} 
        onClose={close} 
        title="Confirm Broadcast" 
        centered 
        radius="md"
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to broadcast this email to all active subscribers? This action cannot be undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" color="gray" onClick={close}>Cancel</Button>
            <Button 
              color="teal" 
              onClick={handleSend}
              className="bg-gradient-to-r from-teal-600 to-cyan-600"
            >
              Confirm and Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
